import { Utensils, Sparkles, Store } from "lucide-react";
import i18n from "@/i18n";
import {
  ActivityCategory,
  MenuCategory,
  type ActivityCategory as ActivityCategoryType,
} from "@/types";

/**
 * Application constants
 */

// Activity type configuration (icons, colors)
export const ACTIVITY_TYPE_CONFIG = {
  [ActivityCategory.RESTAURANT]: {
    icon: Utensils,
    emoji: "🍽️",
    color: "accent",
  },
  [ActivityCategory.ATTRACTION]: {
    icon: Sparkles,
    emoji: "🎯",
    color: "primary",
  },
  [ActivityCategory.GENERIC_SERVICE]: {
    icon: Store,
    emoji: "🛎️",
    color: "success",
  },
} as const;

export const ACTIVITY_TYPE_ORDER: ActivityCategoryType[] = [
  ActivityCategory.RESTAURANT,
  ActivityCategory.GENERIC_SERVICE,
  ActivityCategory.ATTRACTION,
];

export function activityTypeLabel(category: ActivityCategoryType): string {
  return i18n.t(`activityType.${category}`, { ns: "constants" });
}

// Cuisine types for restaurants (values only; labels via i18n)
export const CUISINE_TYPE_VALUES = [
  "italian",
  "spanish",
  "canarian",
  "mediterranean",
  "japanese",
  "chinese",
  "thai",
  "indian",
  "mexican",
  "american",
  "french",
  "greek",
  "middle_eastern",
  "seafood",
  "steakhouse",
  "vegetarian",
  "pizza",
  "sushi",
  "ramen",
  "tapas",
  "fast_food",
  "cafe",
  "fusion",
  "international",
  "other",
] as const;

export type CuisineType = (typeof CUISINE_TYPE_VALUES)[number];

export function cuisineLabel(value: CuisineType): string {
  return i18n.t(`cuisine.${value}`, { ns: "constants" });
}

export function getCuisineSelectOptions(): {
  value: CuisineType;
  label: string;
}[] {
  return CUISINE_TYPE_VALUES.map((value) => ({
    value,
    label: cuisineLabel(value),
  }));
}

// Menu categories for restaurants — emoji map keyed by shared MenuCategory type
export const MENU_CATEGORY_EMOJI: Record<MenuCategory, string> = {
  [MenuCategory.APPETIZER]: "🥗",
  [MenuCategory.FIRST_COURSE]: "🍝",
  [MenuCategory.SECOND_COURSE]: "🥩",
  [MenuCategory.SIDE_DISH]: "🥔",
  [MenuCategory.SALAD]: "🥬",
  [MenuCategory.PIZZA]: "🍕",
  [MenuCategory.SOUP]: "🍲",
  [MenuCategory.DESSERT]: "🍰",
  [MenuCategory.FRUIT]: "🍇",
  [MenuCategory.CHEESE]: "🧀",
  [MenuCategory.BEVERAGE]: "🥤",
  [MenuCategory.WINE]: "🍷",
  [MenuCategory.COCKTAIL]: "🍹",
  [MenuCategory.COFFEE]: "☕",
  [MenuCategory.KIDS_MENU]: "🧒",
  [MenuCategory.SPECIAL]: "⭐",
};

export const MENU_CATEGORIES = Object.values(MenuCategory).map((value) => ({
  value,
  emoji: MENU_CATEGORY_EMOJI[value],
}));

export function isMenuCategory(value: string): value is MenuCategory {
  return Object.values(MenuCategory).some((category) => category === value);
}

export function menuCategoryLabel(category: MenuCategory): string {
  return i18n.t(`menuCategory.${category}`, { ns: "constants" });
}

export function menuCategoryDisplayLabel(category: MenuCategory): string {
  const emoji = MENU_CATEGORY_EMOJI[category];
  return emoji
    ? `${emoji} ${menuCategoryLabel(category)}`
    : menuCategoryLabel(category);
}
